import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import DatePicker from 'react-datepicker';
import DocumentInput from "../common/form/DocumentInput";
import { NavLink, useNavigate } from "react-router-dom";
import { usePostMaillingRuleMutation } from "../../../../services/api/repositories/MaillingRuleRepository";
import TemplateInput from "../common/form/TemplateInput";
import TriggerInput from "../common/form/TriggerInput";
import ProductInput from "../common/form/ProductInput";
import ClientInput from "../common/form/ClientInput";
import { IClientType } from "../../../../interfaces/api/IClientsType";

export default function MailingRuleCreateSection() {
    const navigate = useNavigate();

    const [postMaillingRule, postMaillingRuleData] = usePostMaillingRuleMutation();

    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [endDate, setEndDate] = useState<Date | null>();
    const [trigger, setTrigger] = useState<string>("none");
    const [template, setTemplate] = useState<string>("none");

    const nameRef = useRef<HTMLInputElement>(null);
    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const statusRef = useRef<HTMLInputElement>(null);

    const [productList, setProductList] = useState<string[]>([]);
    const [documentUuidList, setDocumentUuidList] = useState<string[]>([]);

    const [clientTypeList, setClientTypeList] = useState<IClientType[]>([]);
    const [clientTypeUuidList, setclientTypeUuidList] = useState<string[]>([]);

    const handleProductRemove = (value: string) => {
        setProductList(productList.filter(item => item !== value));
    }

    const handleProductAdd = (value: string) => {
        if (productList.includes(value)) {
            return;
        }
        setProductList([...productList, value]);
    }

    const handleDocumentUuidAdd = (uuid: string) => {
        if (documentUuidList.includes(uuid)) {
            return;
        }
        setDocumentUuidList([...documentUuidList, uuid]);
    }

    const handleDocumentUuidRemove = (uuid: string) => {
        setDocumentUuidList(documentUuidList.filter(item => item !== uuid));
    }

    const handleClientTypeAdd = (clientType: IClientType) => {
        if (clientTypeList.some(existingClientType => existingClientType.uuid === clientType.uuid)) {
            return;
        }
        setClientTypeList([...clientTypeList, clientType]);
    };

    const handleClientTypeRemove = (value: string) => {
        setClientTypeList(clientTypeList.filter(item => item.uuid !== value));
        setclientTypeUuidList(clientTypeUuidList.filter(item => item !== value));
    }

    const handleClientTypeUuidAdd = (uuid: string) => {
        if (clientTypeUuidList.some(existingUuidClient => existingUuidClient === uuid)) {
            return;
        }
        setclientTypeUuidList([...clientTypeUuidList, uuid]);
    };

    const [show, setShow] = useState(false);
    const [errMsg, setErrMsg] = useState<string>("");

    const [showValidation, setShowValidation] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ name: string, description: string, client_types: string, trigger: string, template: string, dateEnd: string }>({ name: "", description: "", client_types: "", trigger: "", template: "", dateEnd: "" });
    const [isSetByUser, setIsSetByUser] = useState<{ name: boolean, description: boolean, client_types: boolean, trigger: boolean, template: boolean, dateEnd: boolean }>({ name: false, description: false, client_types: false, trigger: false, template: false, dateEnd: false });

    const handleClose = useCallback(() => {
        setShow(false)
    }, [setShow]);

    const handleShow = useCallback(async () => {

        setShowValidation(true);

        let errors = validationErrors;
        let canSend = true;

        if (nameRef.current?.value === '') {
            errors.name = "Le nom est obligatoire";
            canSend = false;
        }

        if (descriptionRef.current?.value.length === 0) {
            errors.description = "Veuillez saisir une description";
            setIsSetByUser(prevState => ({ ...prevState, description: true }));
            canSend = false;
        }

        if (descriptionRef.current?.value && descriptionRef.current?.value.length > 255) {
            errors.description = "La description ne doit pas dépasser 255 caractères";
            setIsSetByUser(prevState => ({ ...prevState, description: true }));
            canSend = false;
        }

        if (trigger === "none" && !isSetByUser.trigger) {
            errors.trigger = "Veuillez sélectionner un déclencheur";
            setIsSetByUser(prevState => ({ ...prevState, trigger: true }));
            canSend = false;
        }

        if (template === "none" && !isSetByUser.template) {
            errors.template = "Veuillez sélectionner un template";
            setIsSetByUser(prevState => ({ ...prevState, template: true }));
            canSend = false;
        }

        if (clientTypeList.length === 0) {
            errors.client_types = "Veuillez sélectionner au moins un client cible";
            canSend = false;
        }

        if (startDate && endDate && startDate !== null && endDate !== null && startDate > endDate) {
            errors.dateEnd = "La date de fin doit être postérieure à la date de début";
            setIsSetByUser(prevState => ({ ...prevState, dateEnd: true }));
            canSend = false;
        }

        let start_date = null;

        if (startDate && startDate !== null) {
            start_date = `${startDate?.toLocaleDateString()} 00:00:00`;
        } else {
            start_date = `${new Date().toLocaleDateString()} 00:00:00`;
        }

        let end_date = null;
        if (endDate && endDate !== null) {
            end_date = `${endDate?.toLocaleDateString()} 23:59:59`;
        }

        if (!canSend) {
            setValidationErrors(errors);
            return;
        }

        setShow(true)
    }, [validationErrors, trigger, isSetByUser.trigger, isSetByUser.template, template, clientTypeList.length, startDate, endDate]);

    const handleSubmit = useCallback(async () => {

        let start_date = null;

        if (startDate && startDate !== null && startDate !== undefined) {
            start_date = `${startDate.toLocaleDateString()} 00:00:00`;
        } else {
            start_date = `${new Date().toLocaleDateString()} 00:00:00`;
        }

        let end_date = null;
        if (endDate && endDate !== null && endDate !== undefined) {
            end_date = `${endDate.toLocaleDateString()} 23:59:59`;
        }

        let status = 'inactive';
        if (statusRef.current && statusRef.current.checked) {
            status = 'active';
        }

        const documents = documentUuidList.map((uuid) => { return { uuid: uuid } });
        const products = productList.map((reference) => { return { reference: reference } });

        postMaillingRule(
            {
                name: (nameRef.current ? nameRef.current.value : ''),
                description: (descriptionRef.current ? descriptionRef.current.value : ''),
                status: status,
                client_types: clientTypeUuidList,
                start_date: start_date,
                end_date: end_date,
                trigger_uuid: (trigger !== "none" ? trigger : ''),
                template_uuid: (template !== "none" ? template : ''),
                documents: documents,
                products: products,
            }
        );
        setShow(false);
    }, [startDate, endDate, documentUuidList, productList, postMaillingRule, clientTypeUuidList, trigger, template]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (validationErrors.name && value.length > 0) {
            setValidationErrors({ ...validationErrors, name: "" });
        }
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;

        if (validationErrors.description && value.length > 0) {
            setValidationErrors({ ...validationErrors, description: "" });
        }
    }

    const handleError = useCallback(() => {
        if (postMaillingRuleData.isError && postMaillingRuleData.error !== undefined && postMaillingRuleData.error !== null && typeof postMaillingRuleData.error === 'object' && (errMsg === "" || errMsg === "Une erreur a été rencontrée")) {
            if ("error" in postMaillingRuleData.error) {
                setErrMsg(postMaillingRuleData.error.error as string);
            } else if ("data" in postMaillingRuleData.error && typeof postMaillingRuleData.error.data === 'object' && postMaillingRuleData.error.data !== null && "message" in postMaillingRuleData.error.data) {
                setErrMsg(postMaillingRuleData.error.data.message as string);
            }
        } else if (errMsg === "") {
            setErrMsg("Une erreur a été rencontrée");
        }
    }, [postMaillingRuleData.isError, postMaillingRuleData.error, errMsg]);

    useEffect(() => {
        if (postMaillingRuleData.isSuccess && "mailing_rule" in postMaillingRuleData.data) {
            navigate(`/mailing-rules/${postMaillingRuleData.data.mailing_rule.uuid}`);
        }

        if (postMaillingRuleData.isError) {
            handleError();
        }
    }, [postMaillingRuleData, navigate, handleError]);

    return (
        <>
            <div className="object mailing">
                <Form className="form" validated={showValidation}>
                    <section className="form-section id">
                        <h3><span className="icon"></span><span className="text">Définition</span></h3>

                        <Form.Group className="form-group mailing-name required">
                            <Form.Label className="form-label" htmlFor="mailing-name-input">Nom</Form.Label>
                            <Form.Control
                                type="text"
                                className="form-control w100"
                                id="mailing-name-input"
                                required
                                tabIndex={1}
                                placeholder=""
                                ref={nameRef}
                                onChange={handleNameChange}
                                isInvalid={validationErrors.name.length > 0}
                            />
                            <Form.Control.Feedback type="invalid">{validationErrors.name}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="form-group mailing-description">
                            <Form.Label className="form-label" htmlFor="mailing-description-input">Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                className="form-control w100"
                                id="mailing-description-input"
                                tabIndex={2}
                                maxLength={255}
                                ref={descriptionRef}
                                onChange={handleDescriptionChange}
                                isInvalid={validationErrors.description.length > 0}
                            />
                            <Form.Control.Feedback type="invalid">{validationErrors.description}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="form-group mailing-status required">
                            <Form.Label className="form=label" htmlFor="mailing-status-checkbox">Statut</Form.Label>
                            <div className="form-switch status">
                                <Form.Control
                                    type="checkbox"
                                    className="form-switch-checkbox"
                                    id="mailing-status-checkbox"
                                    tabIndex={3}
                                    ref={statusRef}
                                />
                                <Form.Label className="form-switch-label" htmlFor="mailing-status-checkbox">
                                    <span className="icon"></span>
                                    <span className="text">
                                        <span className="on">Actif</span>
                                        <span className="off">Inactif</span>
                                    </span>
                                </Form.Label>
                            </div>
                        </Form.Group>

                        <ClientInput
                            clientTypeList={clientTypeList}
                            handleClientTypeAdd={handleClientTypeAdd}
                            handleClientTypeUuidAdd={handleClientTypeUuidAdd}
                            handleClientTypeRemove={handleClientTypeRemove}
                        />

                    </section>

                    <section className="form-section schedule">
                        <h3><span className="icon"></span><span className="text">Programmation</span></h3>
                        <div className="fromTo">
                            <Form.Group className="form-group mailing-runDateStart">
                                <Form.Label className="form-label" htmlFor="mailing-runDateStart-input">Du</Form.Label>
                                <DatePicker className="form-control w100" id="mailing-runDateStart-input" tabIndex={4} dateFormat="dd/MM/yyyy"
                                    selected={startDate}
                                    onChange={(date) => { setStartDate(date) }} />
                            </Form.Group >
                            <Form.Group className="form-group mailing-runDateEnd">
                                <Form.Label className="form-label" htmlFor="mailing-runDateEnd-input">Au</Form.Label>

                                <DatePicker
                                    className="form-control w100"
                                    id="mailing-runDateEnd-input"
                                    tabIndex={5}
                                    dateFormat="dd/MM/yyyy"
                                    selected={endDate}
                                    onChange={(date) => { setEndDate(date) }}
                                />

                                <Form.Control.Feedback type="invalid">{validationErrors.dateEnd}</Form.Control.Feedback>
                            </Form.Group >

                        </div>
                    </section>

                    <section className="form-section trigger">
                        <h3><span className="icon"></span><span className="text">Déclenchement</span></h3>
                        <TriggerInput
                            tabIndex={6}
                            setTrigger={setTrigger}
                            trigger={trigger}
                            triggerValidationError={validationErrors.trigger}
                            triggerIsSetByUser={isSetByUser.trigger}
                            setTriggerIsSetByUser={(triggerIsSetByUser: boolean) => setIsSetByUser(prevState => ({ ...prevState, trigger: triggerIsSetByUser }))}
                        />
                        <ProductInput productList={productList} handleProductAdd={handleProductAdd} handleProductRemove={handleProductRemove} />
                    </section>

                    <section className="form-section email">
                        <h3><span className="icon"></span><span className="text">Email</span></h3>
                        <TemplateInput
                            tabIndex={9}
                            setTemplate={setTemplate}
                            template={template}
                            templateValidationError={validationErrors.template}
                            templateIsSetByUser={isSetByUser.template}
                            setTemplateIsSetByUser={(templateIsSetByUser: boolean) => setIsSetByUser(prevState => ({ ...prevState, template: templateIsSetByUser }))}
                        />
                        <DocumentInput
                            documentUuidList={documentUuidList}
                            handleDocumentUuidAdd={handleDocumentUuidAdd}
                            handleDocumentUuidRemove={handleDocumentUuidRemove}
                        />
                    </section>

                    <div>

                        {(validationErrors.client_types) && (
                            <Alert
                                variant="danger"
                                dismissible>
                                {validationErrors.client_types}
                            </Alert>
                        )}

                        {(validationErrors.dateEnd) && (
                            <Alert
                                variant="danger"
                                dismissible>
                                {validationErrors.dateEnd}
                            </Alert>
                        )}

                        {(postMaillingRuleData.isError && postMaillingRuleData.data !== undefined && "message" in postMaillingRuleData.data) && (
                            <Alert
                                variant="danger"
                                dismissible>
                                {postMaillingRuleData.data.message}
                            </Alert>
                        )}

                        {postMaillingRuleData.isError && errMsg.length > 0
                            && (
                                <>
                                    <Alert
                                        variant="danger"
                                        dismissible >
                                        {errMsg}
                                    </Alert>
                                </>
                            )}
                    </div>



                    <div className="form-submit">
                        <NavLink to={'/mailing-rules'} type="button" className="btn btn-secondary btn-cancel" tabIndex={11} title="Annuler">
                            <span className="icon"></span>
                            <span className="text">Annuler</span>
                        </NavLink>
                        <Button type="button" className="btn btn-primary btn-ok" data-bs-toggle="modal" data-bs-target="#mailingCreateConfirmationModal" tabIndex={10} title="Ajouter la nouvelle configuration d'envoi" onClick={() => { handleShow() }}>
                            <span className="icon"></span>
                            <span className="text">Ajouter</span>
                        </Button>
                    </div>
                </Form>
            </div>
            <SubmitModal
                show={show}
                handleClose={handleClose}
                handleSubmit={handleSubmit}
                name={nameRef.current ? nameRef.current.value : ''}
                status={statusRef.current && statusRef.current.value === "active" ? 'Actif' : 'Inactif'}
                startDate={(startDate !== undefined && startDate !== null) ? startDate.toLocaleDateString() : null}
                endDate={(endDate !== undefined && endDate !== null) ? endDate.toLocaleDateString() : null} />
        </>
    );
}

function SubmitModal(
    { show, handleClose, handleSubmit, name, status, startDate, endDate }:
        { show: boolean, handleClose: () => void, handleSubmit: () => void, name: string, status: string, startDate: string | null, endDate: string | null }) {

    return (
        <Modal id="mailingCreateConfirmationModal" show={show} onHide={() => { handleClose() }} s>
            <Modal.Header closeButton>
                <h1 className="modal-title" id="mailingCreateConfirmationModalLabel"><span className="icon"></span><span className="text">Création d'une Configuration d'Envoi</span></h1>
            </Modal.Header>
            <Modal.Body>
                <p>Vous souhaitez créer la nouvelle Configuration d'envoi&nbsp;:</p>
                <div className="object mailing" data-status="active">
                    <h2>
                        <span className="icon"></span>
                        <span className="text">{name}</span>
                    </h2>
                    <div className="object-status" data-status="active">
                        <span className="icon"></span>
                        <span className="text">{status}</span>
                    </div>
                    <p>En exploitation à partir du {(startDate !== undefined && startDate !== null) && (<>{startDate}</>)} {(endDate !== undefined && endDate !== null) && (<>jusqu'au {endDate}</>)}</p>
                </div>
                <p>Veuillez valider la création de cette Configuration d'envoi.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    className="btn btn-secondary btn-cancel"
                    data-bs-dismiss="modal"
                    title="Annuler"
                    onClick={() => { handleClose() }}
                >
                    <span className="icon"></span>
                    Annuler
                </Button>
                <Button
                    className="btn btn-primary btn-ok"
                    data-bs-dismiss="modal"
                    title="Valider"
                    onClick={() => { handleSubmit() }}
                >
                    <span className="icon"></span>
                    Valider
                </Button>
            </Modal.Footer>
        </Modal>
    );
}